import { Directive, ElementRef, Renderer2, Input, OnInit } from '@angular/core';

export type NcPositionType = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

@Directive({
	selector: '[ncRibbon]'
})
export class RibbonDirective implements OnInit {
	/** 丝带内容 */
	@Input() ncContent: string = '';
	/** 内容颜色 */
	@Input() ncContentColor: string = '#fff';
	/** 大小，容器宽高较小值的百分比 */
	@Input() ncSize: number = 0.3;
	/** 丝带颜色，需传入十六进制颜色值 */
	@Input() ncRibbonColor: string = '#ff6547';
	/** 丝带位置 */
	@Input() ncPosition: NcPositionType = 'top-left';

	private ribbonContainer!: HTMLElement;
	private ribbonSize!: number;
	private uniqueId: string;

	constructor(private el: ElementRef, private renderer: Renderer2) {
		// 生成唯一ID
		this.uniqueId = `ribbon_${Math.random().toString(36).substring(2, 11)}`;
	}

	ngOnInit() {
		this.createRibbonContainer();
	}

	private createRibbonContainer() {
		const elementPosition = getComputedStyle(this.el.nativeElement).position;
		if (!elementPosition || elementPosition === 'static') {
			this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
		}

		// 创建丝带容器
		this.ribbonContainer = this.renderer.createElement('div');
		this.renderer.addClass(this.ribbonContainer, this.uniqueId);
		this.renderer.setStyle(this.ribbonContainer, 'position', 'absolute');
		this.renderer.setStyle(this.ribbonContainer, 'overflow', 'hidden');
		this.renderer.setStyle(this.ribbonContainer, 'display', 'flex');
		this.renderer.setStyle(this.ribbonContainer, 'justify-content', 'center');
		this.renderer.setStyle(this.ribbonContainer, 'align-items', 'center');

		// 添加到DOM
		this.renderer.appendChild(this.el.nativeElement, this.ribbonContainer);

		// 初始渲染
		this.updateRibbonSize();

		// 使用MutationObserver监听父容器尺寸变化
		this.observeParentSize();
	}

	private updateRibbonSize() {
		// 获取父容器的尺寸
		const parentWidth = this.el.nativeElement.offsetWidth;
		const parentHeight = this.el.nativeElement.offsetHeight;

		// 如果父容器尺寸为0，延迟重试
		if (parentWidth === 0 || parentHeight === 0) {
			setTimeout(() => this.updateRibbonSize(), 100);
			return;
		}

		this.ribbonSize = Math.min(parentWidth, parentHeight) * this.ncSize;

		// 更新丝带容器尺寸
		this.renderer.setStyle(this.ribbonContainer, 'width', `${this.ribbonSize}px`);
		this.renderer.setStyle(this.ribbonContainer, 'height', `${this.ribbonSize}px`);

		// 更新位置
		const offset = -(this.ribbonSize * 0.06);
		switch (this.ncPosition) {
			case 'top-right':
				this.renderer.setStyle(this.ribbonContainer, 'top', `${offset}px`);
				this.renderer.setStyle(this.ribbonContainer, 'right', `${offset}px`);
				break;
			case 'bottom-left':
				this.renderer.setStyle(this.ribbonContainer, 'bottom', `${offset}px`);
				this.renderer.setStyle(this.ribbonContainer, 'left', `${offset}px`);
				break;
			case 'bottom-right':
				this.renderer.setStyle(this.ribbonContainer, 'bottom', `${offset}px`);
				this.renderer.setStyle(this.ribbonContainer, 'right', `${offset}px`);
				break;
			default: //top-left
				this.renderer.setStyle(this.ribbonContainer, 'top', `${offset}px`);
				this.renderer.setStyle(this.ribbonContainer, 'left', `${offset}px`);
				break;
		}

		// 更新CSS样式
		this.updateRibbonStyles();
	}

	private updateRibbonStyles() {
		// 根据输入的颜色计算渐变色
		const mainColor = this.ncRibbonColor;
		const lighterColor = this.adjustColor(this.ncRibbonColor, 45);
		const darkerColor = this.adjustColor(this.ncRibbonColor, -10);

		const cssText = `
			.${this.uniqueId}::before {
				content: '${this.ncContent}';
				position: absolute;
				width: 150%;
				height: ${this.ribbonSize * 0.3}px;
				background: linear-gradient(45deg, ${mainColor} 0%, ${lighterColor} 51%, ${darkerColor} 100%);
				transform: rotate(${this.getRotationDegree()}) translateY(${-this.ribbonSize * 0.16}px) ${this.getTextRotation()};
				display: flex;
				align-items: center;
				justify-content: center;
				color: ${this.ncContentColor};
				font-weight: 600;
				font-size: ${Math.ceil(this.ribbonSize * 0.18)}px;
				letter-spacing: 0.1em;
				box-shadow: ${this.getRibbonShadow()};
			}

			.${this.uniqueId}::after {
				content: '';
				position: absolute;
				width: ${this.ribbonSize * 0.1}px;
				height: ${this.ribbonSize * 0.1}px;
				z-index: -1;
				${this.getShadowPosition()}
				background: linear-gradient(45deg, ${mainColor} 0%, ${lighterColor} 51%, ${mainColor} 100%);
			}
		`;

		// 移除旧样式（如果存在）
		const oldStyle = document.querySelector(`style[data-ribbon-id="${this.uniqueId}"]`);
		if (oldStyle) {
			oldStyle.remove();
		}

		// 添加新样式
		const styleElement = this.renderer.createElement('style');
		this.renderer.setAttribute(styleElement, 'data-ribbon-id', this.uniqueId);
		this.renderer.appendChild(styleElement, this.renderer.createText(cssText));
		this.renderer.appendChild(document.head, styleElement);
	}

	// 添加颜色调整方法
	private adjustColor(color: string, percent: number): string {
		// 确保颜色值格式统一，移除可能存在的 # 号
		const hex = color.replace(/^#/, '');

		// 验证颜色值格式（6位或8位）
		if (!/^[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(hex)) {
			console.warn('Invalid color format. Using default color.');
			return '#ff6547';
		}

		// 分离颜色值和透明度
		const colorHex = hex.slice(0, 6);
		const alpha = hex.length === 8 ? hex.slice(6, 8) : '';

		const r = parseInt(colorHex.substring(0, 2), 16);
		const g = parseInt(colorHex.substring(2, 4), 16);
		const b = parseInt(colorHex.substring(4, 6), 16);

		const adjustValue = (value: number): number => {
			const newValue = value + (value * percent / 100);
			return Math.min(255, Math.max(0, Math.round(newValue)));
		};

		const nr = adjustValue(r);
		const ng = adjustValue(g);
		const nb = adjustValue(b);

		// 如果原始颜色包含透明度，则在结果中也包含透明度
		return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}${alpha}`;
	}

	// 添加获取旋转角度的方法
	private getRotationDegree(): string {
		switch (this.ncPosition) {
			case 'top-right':
				return '45deg';
			case 'bottom-left':
				return '-135deg';
			case 'bottom-right':
				return '135deg';
			default: // top-left
				return '-45deg';
		}
	}

	// 添加获取文字旋转的方法
	private getTextRotation(): string {
		switch (this.ncPosition) {
			case 'bottom-left':
			case 'bottom-right':
				return 'rotate(180deg)';
			default:
				return '';
		}
	}

	// 添加获取丝带阴影的方法
	private getRibbonShadow(): string {
		switch (this.ncPosition) {
			case 'bottom-left':
			case 'bottom-right':
				return `0 -${this.ribbonSize * 0.05}px ${this.ribbonSize * 0.1}px rgba(0, 0, 0, 0.23)`;
			default:
				return `0 ${this.ribbonSize * 0.05}px ${this.ribbonSize * 0.1}px rgba(0, 0, 0, 0.23)`;
		}
	}

	// 添加获取阴影位置的方法
	private getShadowPosition(): string {
		const shadowSize = this.ribbonSize * 0.9 + 'px';
		const shadowColor = this.adjustColor(this.ncRibbonColor, -20);

		switch (this.ncPosition) {
			case 'top-right':
				return `bottom: 0; right: 0; box-shadow: -${shadowSize} -${shadowSize} ${shadowColor};`;
			case 'bottom-left':
				return `top: 0; left: 0; box-shadow: ${shadowSize} ${shadowSize} ${shadowColor};`;
			case 'bottom-right':
				return `top: 0; right: 0; box-shadow: -${shadowSize} ${shadowSize} ${shadowColor};`;
			default: // top-left
				return `bottom: 0; left: 0; box-shadow: ${shadowSize} -${shadowSize} ${shadowColor};`;
		}
	}

	// 监听父容器尺寸变化
	private observeParentSize() {
		// 使用ResizeObserver监听尺寸变化（现代浏览器支持）
		if (typeof ResizeObserver !== 'undefined') {
			const resizeObserver = new ResizeObserver(() => {
				this.updateRibbonSize();
			});
			resizeObserver.observe(this.el.nativeElement);
		} else {
			// 降级方案：使用MutationObserver监听DOM变化
			const observer = new MutationObserver(() => {
				this.updateRibbonSize();
			});
			observer.observe(this.el.nativeElement, {
				childList: true,
				subtree: true,
				attributes: true
			});

			// 额外的安全措施：定期检查尺寸变化
			let lastWidth = this.el.nativeElement.offsetWidth;
			let lastHeight = this.el.nativeElement.offsetHeight;

			const checkSizeInterval = setInterval(() => {
				const currentWidth = this.el.nativeElement.offsetWidth;
				const currentHeight = this.el.nativeElement.offsetHeight;

				if (currentWidth !== lastWidth || currentHeight !== lastHeight) {
					lastWidth = currentWidth;
					lastHeight = currentHeight;
					this.updateRibbonSize();
				}
			}, 500);

			// 组件销毁时清除定时器
			this.el.nativeElement.addEventListener('DOMNodeRemoved', () => {
				clearInterval(checkSizeInterval);
			});
		}
	}
}